import React, { useState, useRef, useEffect } from 'react';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const GRADE_OPTIONS = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'F'];
const COLORS = { 
  'A+': '#4CAF50', 'A': '#4CAF50', 'A-': '#4CAF50',
  'B+': '#2196F3', 'B': '#2196F3', 'B-': '#2196F3',
  'C+': '#FFEB3B', 'C': '#FFEB3B', 'C-': '#FFEB3B',
  'D+': '#FF9800', 'D': '#FF9800', 'D-': '#FF9800',
  'F': '#F44336', '': '#eee'
};

export default function GradeCell({
  date, 
  userId, 
  currentGrade,
  compact = false, 
  dayLabel = null,
  monthLabel = null,
  readOnly = false
}) {
  const { theme } = useTheme();
  const [grade, setGrade] = useState(currentGrade || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const cellRef = useRef(null);
  const [saving, setSaving] = useState(false);
  
  // Update local state when props change
  useEffect(() => {
    if (currentGrade !== undefined) {
      setGrade(currentGrade);
    }
  }, [currentGrade]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          cellRef.current && !cellRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    
    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Cleanup the event listener
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const save = async (newGrade) => {
    // Optimistically update UI
    setGrade(newGrade);
    setShowDropdown(false);
    
    if (saving) return; // Prevent double-saves
    
    try {
      setSaving(true);
      console.log(`Saving ${date} grade: ${newGrade}`);
      const response = await api.post(`users/${userId}/grades/`, { 
        date: date, 
        grade: newGrade 
      });
      console.log('Grade saved successfully:', response.data);
    } catch (err) {
      console.error('Failed to save grade:', err.response || err);
      alert('Save failed – check console for details.');
      setGrade(currentGrade); // Roll back on error
    } finally {
      setSaving(false);
    }
  };

  const handleCellClick = (e) => {
    e.stopPropagation();
    if (!readOnly) {
      setShowDropdown(!showDropdown);
    }
  };

  // Calculate background color based on grade
  const backgroundColor = grade
    ? COLORS[grade.charAt(0)] || COLORS['']
    : (theme === 'dark' ? '#444' : COLORS['']);

  // Adjust cell height based on compact mode
  const cellHeight = compact ? 30 : 40;
  
  return (
    <div
      ref={cellRef}
      style={{
        border: '1px solid #ccc',
        height: cellHeight,
        background: backgroundColor,
        position: 'relative',
        cursor: readOnly ? 'default' : 'pointer',
        borderRadius: '4px',
        overflow: 'visible' // Changed from 'hidden' to allow dropdown to be visible
      }}
      title={date}
      onClick={handleCellClick}
    >
      {/* Month Label (for yearly view) */}
      {monthLabel && (
        <div style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          background: 'rgba(0,0,0,0.1)',
          fontSize: '10px',
          textAlign: 'center',
          padding: '1px 0'
        }}>
          {monthLabel}
        </div>
      )}
      
      {/* Day Number */}
      <small style={{ 
        position: 'absolute', 
        top: monthLabel ? 12 : 2, 
        left: 2, 
        fontSize: compact ? '9px' : '11px',
        opacity: 0.7
      }}>
        {dayLabel || date.slice(-2)}
      </small>
      
      {/* Grade Display */}
      {grade && !compact && (
        <div style={{ textAlign: 'center', lineHeight: `${cellHeight}px` }}>
          {grade}
        </div>
      )}
      
      {/* Compact Grade Display */}
      {grade && compact && (
        <div style={{ 
          position: 'absolute',
          bottom: 2,
          right: 2,
          fontSize: '10px',
          fontWeight: 'bold'
        }}>
          {grade}
        </div>
      )}
      
      {/* Grade Selection Dropdown */}
      {showDropdown && (
        <div 
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: compact ? 'calc(100% + 5px)' : 'calc(100% + 5px)',
            left: compact ? '-20px' : 0,
            backgroundColor: theme === 'dark' ? '#333' : 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            zIndex: 1000,
            width: compact ? '130px' : '70px',
            display: 'block'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ 
            display: compact ? 'grid' : 'block',
            gridTemplateColumns: compact ? 'repeat(3, 1fr)' : 'auto'
          }}>
            {GRADE_OPTIONS.map(option => (
              <div 
                key={option}
                style={{
                  padding: compact ? '3px' : '5px 10px',
                  cursor: 'pointer',
                  backgroundColor: COLORS[option.charAt(0)],
                  color: option.charAt(0) === 'C' ? 'black' : 'white',
                  textAlign: 'center',
                  fontSize: compact ? '11px' : 'inherit'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  save(option);
                }}
              >
                {option}
              </div>
            ))}
          </div>
          <div
            style={{
              backgroundColor: theme === 'dark' ? '#444' : '#eee',
               cursor: 'pointer',
               color: 'inherit',
              padding: compact ? '3px' : '5px 10px',
              textAlign: 'center'
            }}
            onClick={(e) => {
              e.stopPropagation();
              save('');
            }}
          >
            Clear
          </div>
        </div>
      )}
    </div>
  );
}